'use client';

import React, { useCallback, useMemo, useRef, useState } from 'react';
import { TextField, Button, BlockStack, Box, Text, InlineStack, Spinner, Card } from '@shopify/polaris';

import { ImagesModalSectionProps, sectionToProviderMap } from '@/features/media-modal/components/common/commonTypes';
import { ICardMediaAsset, MediaAsset, MediaAssetType, PlayAppMediaAssetStatus } from '@/types/media';
import { useVideoServiceMediaInfiniteQuery } from '@/features/media-modal/hooks/usePlayAppMediaInfiniteQuery';
import { VIDEO_FRAGMENT_DURATION_MS } from '../../common/VideoAssetPreview';

import ProviderConnector from '@/features/asset-providers/ProviderConnector';
import AssetTypeSelector from '@/features/asset-providers/AssetTypeSelector';
import MediaAssetCard from '@/components/media-asset/MediaAssetCard';
import MediaGrid from '@/components/media-asset/MediaGrid';
import useProviderConnectionStatus from '@/hooks/useProviderConnectionStatus';
import { useCurrentApp } from '@/stores/userDataStore';
type IntegrationAssetModalContentProps = ImagesModalSectionProps & {
  title: string;
};

export const IntegrationAssetModalContent = ({
  section,
  selectedMedia,
  setSelectedMedia,
  title,
  preferredMediaAssetType,
  isOnlyImages = false,
}: IntegrationAssetModalContentProps) => {
  const [assetType, setAssetType] = useState<Exclude<MediaAssetType, MediaAssetType.audio> | undefined>(() => {
    if (isOnlyImages) {
      return MediaAssetType.image;
    }

    if (!preferredMediaAssetType || preferredMediaAssetType === MediaAssetType.audio) {
      return MediaAssetType.video;
    }

    return preferredMediaAssetType;
  });

  const { isLoading: isUserLoading } = useCurrentApp();
  const [queryInput, setQueryInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const provider = sectionToProviderMap[section];

  const { appsByProvider } = useProviderConnectionStatus();
  const {
    assets,
    isLoading: isAssetsLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    invalidateQuery,
  } = useVideoServiceMediaInfiniteQuery({
    query: searchQuery,
    provider,
    type: assetType,
  });

  const isLoading = isUserLoading || isAssetsLoading;

  const app = appsByProvider[provider]?.[0];
  const isConnected = !!app;
  const isFiltersApplied = !!searchQuery || !!assetType;
  const isEmptyResponse = !assets.length && !isLoading;

  const selectedUrls = useMemo(() => new Set(selectedMedia.map((media) => media.src)), [selectedMedia]);

  const onAssetClick = useCallback((asset: ICardMediaAsset) => {
    const alreadySelected = selectedUrls.has(asset.src!);

    if (alreadySelected) {
      return setSelectedMedia((prev) => prev.filter((existing) => existing.src !== asset.src));
    }

    if (asset.type === MediaAssetType.video) {
      const newVideo = {
        ...asset,
        settings: {
          playFrom: 0,
          playTill: VIDEO_FRAGMENT_DURATION_MS,
          duration: VIDEO_FRAGMENT_DURATION_MS,
        },
      } as MediaAsset;

      return setSelectedMedia((prev) => [...prev, newVideo]);
    }

    setSelectedMedia((prev) => [...prev, asset as MediaAsset]);
  }, []);

  const renderItem = useCallback(
    (asset) => {
      if (asset.status === PlayAppMediaAssetStatus.Uploading) {
        return (
          <InlineStack align="center">
            <Spinner accessibilityLabel={`Loading ${asset.type}`} size="large" />
          </InlineStack>
        );
      }
      return (
        <MediaAssetCard
          size="small"
          hideUseAction
          aspectRatio="portrait"
          media={asset}
          key={asset.id}
          selected={selectedUrls.has(asset.src)}
          onSelect={onAssetClick}
        />
      );
    },
    [onAssetClick, selectedUrls]
  );

  const handleLoadMore = useCallback(() => {
    if (hasNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, fetchNextPage]);

  const customScrollParentRef = useRef<HTMLDivElement>(null);

  return (
    <BlockStack gap="400">
      <Text as="h2" variant="headingMd">
        {title}
      </Text>

      <Card>
        <BlockStack gap="200">
          <Text as="h3" variant="headingSm" tone="subdued">
            Filters
          </Text>
          <InlineStack gap="400">
            {!isOnlyImages && <AssetTypeSelector assetType={assetType} setAssetType={setAssetType} />}

            <TextField
              label="Name"
              value={queryInput}
              onChange={setQueryInput}
              autoComplete="off"
              placeholder="Name search..."
              connectedRight={<Button onClick={() => setSearchQuery(queryInput)}>Search</Button>}
            />
          </InlineStack>
        </BlockStack>
      </Card>

      {isConnected && isEmptyResponse && isFiltersApplied && (
        <Box padding="400">
          <InlineStack align="center">
            <Text as="h2" variant="headingMd">
              Nothing found. Try another search query...
            </Text>
          </InlineStack>
        </Box>
      )}

      <ProviderConnector provider={provider} assets={assets} isLoading={isLoading} invalidateQuery={invalidateQuery} />

      <div ref={customScrollParentRef}>
        {isLoading && (
          <InlineStack align="center">
            <Spinner accessibilityLabel="Loading assets" size="large" />
          </InlineStack>
        )}

        {!isLoading && <MediaGrid items={assets} size="medium" renderItem={renderItem} endReached={handleLoadMore} />}
      </div>
    </BlockStack>
  );
};
